
import { BaseAction } from "../../../core/actions/BaseAction";
import { ExecutionStatus } from "../../../core/enum/ExecutionStatus";
import { calculatePrice } from "../../../Lib/Utils/validationUtil";
import { updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { TripTypeEnum, TrainTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { IBookedOffer, IBookedTrips, IBookingTypeTrip, IReservation } from "../../../Orchestrator/Types/Index";
import { IDefaultActionResult } from "../../../Orchestrator/Types/Interface/ActionParams/DefaultActionResult";
import { IAlreadyAddonsForPassenger, IAlreadyAddonsLegPassengerAddons } from "../../../Orchestrator/Types/Interface/IAddOns";
import { getBookingAction } from "../../LogicActions/Booking/GetBookingAction";
import { prepareBookingSummaryAction } from "../../LogicActions/Checkout/Summary/PrepareBookingSummaryAction";

export class ProceedToOnHoldAction extends BaseAction<IDefaultActionResult, {
    bookingId: string,
    tripData: IBookedTrips
}> {
    override async run(): Promise<IDefaultActionResult> {
        let actionResult: IDefaultActionResult = {
            data: null,
            message: "",
            status: "fail"
        }
        try {
            const { bookingId, tripData } = this.params!;
            updateRecoilState("displayPassengerDataAtom", tripData.passengers);
            updateRecoilState("selectedTripsDataAtom", tripData);
            updateRecoilState("isManageSeatingAtom", false);
            updateRecoilState("isHyperFastStateAtom", true);
            updateRecoilState("isQuickExchangeStateAtom", true);

            // Pass filtering parameters to GetBookingAction
            getBookingAction.setParams({
                bookingId,
                direction: tripData.direction,
                displayPassengerData: tripData.passengers,
                selectedTripsData: tripData
            });
            await getBookingAction.execute();

            if (getBookingAction.status.executionStatus === ExecutionStatus.SUCCESS) {
                const response: any = getBookingAction.result;
                if (response && response.data && response.data.booking) {
                    prepareBookingSummaryAction.setParams({ ...response.data.booking, isFrom: "on-hold-booking" })
                    await prepareBookingSummaryAction.execute();
                    const filteredBookingPassengers = response.data.booking?.passengers || [];
                    updateRecoilState("passengerStateAtom", null)
                    updateRecoilState("passengerStateAtom", filteredBookingPassengers);
                    let allBookedOfferWithoutExchange: IBookedOffer[] = response?.allBookedOfferWithoutExchange || []
                    let allBookedTripWithoutExchange: IBookingTypeTrip[] = response?.allBookedTripsWithoutExchange || []
                    if (allBookedOfferWithoutExchange.length > 0 && allBookedTripWithoutExchange.length > 0) {
                        const alreadyAddonsLegPassenger: IAlreadyAddonsLegPassengerAddons = {}
                        allBookedOfferWithoutExchange.forEach((bookedOffer: IBookedOffer) => {
                            let bookTrip = allBookedTripWithoutExchange.find(d => d.id === bookedOffer.tripCoverage.coveredTripId)
                            const allAncillaryForTrip: IReservation[] = bookedOffer.ancillaries.filter(d => d.tripCoverage.coveredTripId === bookTrip?.id && d.status !== "REFUNDED")
                            const legWiseAlreadyAddons: IAlreadyAddonsForPassenger[] = []
                            for (const ancillaries of allAncillaryForTrip) {
                                const passengerDetails = filteredBookingPassengers.find((d: any) => d.id === ancillaries.passengerIds[0])
                                const productId = ancillaries.products[0].productId
                                const productDetails = bookedOffer.products.find(d => d.id === productId)
                                const bookTripLeg = bookTrip?.legs.find(d => d.id === ancillaries.tripCoverage.coveredLegIds[0])
                                const passenger: IAlreadyAddonsForPassenger = {
                                    category: ancillaries.type,
                                    addonName: productDetails?.description || "",
                                    passengerId: passengerDetails?.id || "",
                                    passengerName: `${passengerDetails?.detail?.firstName || ""} ${passengerDetails?.detail?.lastName || ""}`,
                                    passengerRef: passengerDetails?.externalRef || "",
                                    type: productDetails?.code || "",
                                    price: calculatePrice(ancillaries?.price) || 0,
                                    stopCode: `${bookTripLeg?.timedLeg.start.stopPlaceRef.stopPlaceRef}-${bookTripLeg?.timedLeg.end.stopPlaceRef.stopPlaceRef}`,
                                    legId: bookTripLeg?.id || "",
                                    ancillaryId: ancillaries.id
                                }
                                legWiseAlreadyAddons.push(passenger)
                            }
                            let direction = bookTrip?.direction || ""
                            direction = direction === TripTypeEnum.IN_BOUND ? TrainTypeEnum.inbound : TrainTypeEnum.outbound
                            alreadyAddonsLegPassenger[direction] = legWiseAlreadyAddons
                        })
                        updateRecoilState("alreadyAddonsLegPassengerAtom", alreadyAddonsLegPassenger)
                        const totalAlreadyPaidPrice = [...alreadyAddonsLegPassenger?.outbound || [], ...alreadyAddonsLegPassenger?.inbound || []]
                        const legTotals = Object.values(
                            totalAlreadyPaidPrice.reduce((acc, item: IAlreadyAddonsForPassenger) => {
                                const key = item.legId ?? "UNKNOWN";
                                acc[key] = acc[key] || { legId: key, totalPrice: 0 };
                                acc[key].totalPrice += item.price || 0;
                                return acc;
                            }, {} as Record<string, { legId: string; totalPrice: number }>)
                        );
                        updateRecoilState("alreadyAddonsLegPassengerTotalAtom", legTotals)
                    }
                }
                updateRecoilState("paymentStateAtom", null);
                updateRecoilState("paymentStateAtom", {
                    // Left unset so <Payment> can preselect the customer's default saved
                    // card — it only preselects while no method is set (Payment.tsx).
                    method: null,
                    total: 0,
                });

                actionResult.status = "success"
            } else {
                console.error("GetBookingAction failed:", getBookingAction.status.error);
                actionResult.message = "Failed to fetch booking data"
            }
        } catch (error) {
            console.error("Error during proceed to on hold action:", error);
            actionResult.message = "Something went wrong"
        } finally {
            return actionResult
        }
    }
}

export const proceedToOnHoldAction = new ProceedToOnHoldAction();
